import React, { useCallback, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Collapse,
  Dropdown,
  MenuProps,
  message,
  Table,
} from "antd";

import MediaPlayer from "components/media-player/media-player";
import RemovePopconfirm from "components/remove-popconfirm/remove-popconfirm";

import { useGetProductByIdQuery } from "graphql/queries/get-product-by-id.generated";
import { useUpdateProductByIdMutation } from "graphql/mutations/update-product-by-id.generated";
import {
  AttachmentType,
  AttachmentTypeDisplayNames,
  AttachmentTypePluralizedDisplayNames,
  MediaType,
  UserRoleValue,
} from "constants/field-values";
import routeNames from "constants/route-names";
import { useProductsFiltersValue } from "state/reactive-vars/products-filters-var";
import { ApolloError } from "@apollo/client";
import StatusIndicator from "components/status-indicator/status-indicator";
import dayjs from "dayjs";
import DropdownArrow from "components/dropdown-arrow/dropdown-arrow";
import { createLabelForFilters, voidFnc } from "utils/helpers";
import { useUpdateInstalledProductByIdMutation } from "graphql/mutations/update-installed-product-by-id.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { getDeviceStatusKey, getDeviceStatusLabel } from "utils/device-status";

const AdminProductDetails: React.FC = () => {
  const { loggedUserValue } = useLoggedUser();
  const { updateProductsFilters } = useProductsFiltersValue();
  const params = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<any>(null);
  const [fileViewer, setFileViewer] = useState<{
    type: MediaType | null;
    src: string;
  }>({ type: null, src: "" });
  const [isLoadingAttachment, setIsLoadingAttachment] =
    useState<boolean>(false);
  const [selectedPropertyFilter, setSelectedPropertyFilter] = useState<
    string[]
  >([]);
  const [productLocationsMap, setProductLocationsMap] = useState<
    Map<string, any[]>
  >(new Map());
  const [propertyOptions, setPropertyOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [propertiesDropdownOpen, setPropertiesDropdownOpen] =
    useState<boolean>(false);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string[]>([]);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState<boolean>(false);
  const collapseLocationRef = useRef<HTMLDivElement>(null);

  const handleOnOpenPDF = useCallback(async (attachment: any) => {
    setIsLoadingAttachment(true);
    if (attachment.document_link) {
      try {
        setFileViewer({
          type: MediaType.Document,
          src: attachment.document_link,
        });
        setIsLoadingAttachment(false);
      } catch (error) {
        console.log("handleOnOpenPDF error: ", error);
      }
    }
  }, []);

  const handleOnOpenVideo = useCallback((attachment: any) => {
    if (attachment.video_link) {
      setFileViewer({ type: MediaType.Video, src: attachment.video_link });
    }

    if (attachment.photo_link) {
      setFileViewer({ type: MediaType.Image, src: attachment.photo_link });
    }
  }, []);

  const handleOnErrorGetProductById = useCallback((error: ApolloError) => {
    console.log("useGetProductByIdQuery Error: ", error);
  }, []);

  const handleOnCompleteGetProductById = useCallback((data: any) => {
    if (data.products_by_pk.length === 0) {
      message.error("Device not found.");
      navigate(routeNames.ADMIN_DEVICES);
      return;
    }

    const uniqueProperties = new Map();
    const separatedProducts = new Map([["ALL", [] as any]]);
    const deviceImg = data?.products_by_pk[0]?.photo_link;

    data?.products_by_pk[0]?.installed_products.forEach((device) => {
      if (device.property) {
        if (!separatedProducts.has(device.property.id)) {
          separatedProducts.set(device.property.id, []);
        }
        const deviceWithSetImg = {
          ...device,
          photo_link: device.photo_link || deviceImg,
        };
        separatedProducts.get(device.property.id)!.push(deviceWithSetImg);
        separatedProducts.get("ALL").push(deviceWithSetImg);
        uniqueProperties.set(device.property.id, device.property.name);
      }
    });

    const newPropertyOptions = Array.from(uniqueProperties.entries()).map(
      ([id, name]) => ({
        value: id,
        label: name,
      })
    );

    setProductLocationsMap(separatedProducts);
    setPropertyOptions(newPropertyOptions);
    setProduct(data?.products_by_pk[0]);
  }, []);

  const { loading: loadingProduct, refetch: refetchProduct } =
    useGetProductByIdQuery({
      skip: !params.deviceId,
      variables: {
        id: params.deviceId,
        is_not_deleted: true,
      },
      errorPolicy: "none",
      fetchPolicy: "network-only",
      nextFetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onError: handleOnErrorGetProductById,
      onCompleted: handleOnCompleteGetProductById,
    });

  const handleOnEditProduct = useCallback(() => {
    const { id } = product || {};

    if (id) {
      const path = `${routeNames.ADMIN_DEVICES_EDIT.replace(
        ":deviceId",
        id
      )}`;

      navigate(path);
    }
  }, [product]);

  const [updateProductById] = useUpdateProductByIdMutation({
    onCompleted: () => {
      message.success("Device removed successfully.");
      navigate(routeNames.ADMIN_DEVICES);
    },
    onError: (error: ApolloError) => {
      message.error("Failed to remove device.");
      console.log("Update Device By Id Error: ", error);
    },
  });

  const handleRemove = useCallback(() => {
    const { id, installed_products } = product || {};

    if (installed_products?.length) {
      message.error(
        `Cannot currently delete this device with ${installed_products.length} active instances.`
      );
      return;
    }

    if (id) {
      updateProductById({
        variables: {
          id: id,
          set: {
            deleted_at: new Date(),
            deleted_by: loggedUserValue.id,
          },
        },
      });
    }
  }, [product, updateProductById]);

  const handlePropertySelection = useCallback(
    (propertyId: string) => {
      if (propertyId === "property-dropdown-option-all") {
        setSelectedPropertyFilter([]);
      } else {
        setSelectedPropertyFilter((prev) => {
          if (prev.includes(propertyId)) {
            return prev.filter((id) => id !== propertyId);
          } else {
            return [...prev, propertyId];
          }
        });
      }
    },
    [setSelectedPropertyFilter]
  );

  const handleTypeSelection = useCallback((type: string) => {
    if (type === "type-dropdown-option-all") {
      setSelectedTypeFilter([]);
    } else {
      setSelectedTypeFilter((prev) => {
        if (prev.includes(type)) {
          return prev.filter((t) => t !== type);
        } else {
          return [...prev, type];
        }
      });
    }
  }, []);

  const filteredInstallDevicesData = useMemo(() => {
    if (
      selectedPropertyFilter.length === 0 ||
      selectedPropertyFilter.length === propertyOptions.length
    ) {
      return productLocationsMap.get("ALL");
    }

    return selectedPropertyFilter.reduce((acc, curr) => {
      const productLocation = productLocationsMap.get(curr);
      if (productLocation) {
        acc = acc.concat(productLocation);
      }
      return acc;
    }, [] as any);
  }, [productLocationsMap, selectedPropertyFilter]);

  const [propertiesOptions, propertyDropdownLabel = "All Properties"] =
    useMemo(() => {
      const allPropertiesSelected =
        selectedPropertyFilter.length === 0 ||
        selectedPropertyFilter.length === propertyOptions.length;
      let selectedPropertyName;

      if (propertyOptions.length) {
        const productProperties: MenuProps["items"] = propertyOptions.reduce(
          (options, property, currentindex) => {
            const optionId = `property-dropdown-option-${currentindex + 1}-${
              property.value
            }`;

            const isSelected = selectedPropertyFilter.includes(property.value);

            if (!selectedPropertyName && isSelected) {
              // property should be defined by this step, if not, maybe refactor to ensure it is
              selectedPropertyName = property.label;
            }

            const newOption = {
              key: `${property.value}`,
              icon: (
                <div>
                  <input
                    className="custom-checkbox"
                    id={optionId}
                    type="checkbox"
                    onClick={(e) => e.stopPropagation()}
                    checked={isSelected}
                    onChange={voidFnc}
                  />
                  <label htmlFor={optionId} className="custom-checkbox-label">
                    {property.label}
                  </label>
                </div>
              ),
            };

            options.push(newOption);

            return options;
          },
          [
            {
              key: "property-dropdown-option-all",
              icon: (
                <div>
                  <input
                    className="custom-checkbox"
                    id="property-dropdown-option-all"
                    type="checkbox"
                    onClick={(e) => e.stopPropagation()}
                    checked={allPropertiesSelected}
                    onChange={voidFnc}
                  />
                  <label
                    htmlFor="property-dropdown-option-all"
                    className="custom-checkbox-label"
                  >
                    All Properties
                  </label>
                </div>
              ),
            },
          ]
        );

        return [
          productProperties,
          createLabelForFilters(
            "All Properties",
            selectedPropertyFilter,
            selectedPropertyName
          ),
        ];
      }

      return [[], "All Properties"];
    }, [propertyOptions, selectedPropertyFilter]);

  // Determine whether to show property column (when all properties are selected)
  const showPropertyColumn = useMemo(() => {
    return (
      selectedPropertyFilter.length === 0 ||
      selectedPropertyFilter.length === propertyOptions.length
    );
  }, [selectedPropertyFilter.length, propertyOptions.length]);

  const [updateInstalledDevices] = useUpdateInstalledProductByIdMutation({
    onCompleted: () => {
      message.success("Installed device removed successfully");
      refetchProduct();
    },
    onError: (error) => {
      message.error("Failed to remove installed device");
      console.error("Error removing installed device:", error);
    },
  });

  const locationsTableColumns = useMemo(
    () => [
      {
        title: showPropertyColumn ? "PROPERTY" : "LOCATION",
        dataIndex: showPropertyColumn ? "property" : "location",
        render: (value, record) => {
          const { photo_link, location, property } = record;
          const displayValue = showPropertyColumn ? property?.name : location;
          const img = photo_link ? (
            <img
              className="locations-table-photo"
              src={photo_link}
              alt="Device"
            />
          ) : null;
          return (
            <>
              {img}
              {displayValue || "N/A"}
            </>
          );
        },
      },
      {
        title: "SERIAL #",
        dataIndex: "product_serial",
        render: (serial) => serial || "N/A",
      },
      {
        title: "INSTALL DATE",
        dataIndex: "installation_date",
        render: (date) => dayjs(date).format("MM/DD/YYYY"),
      },
      {
        title: "STATUS",
        dataIndex: "device_status",
        render: (_, device) => (
          <StatusIndicator
            deviceStatus={getDeviceStatusKey(device)}
            label={getDeviceStatusLabel(device)}
          />
        ),
      },
      {
        dataIndex: "id",
        render: (id) => (
          <Link
            to={routeNames.INSTALLED_DEVICES_DETAILS.replace(
              ":installedDevId",
              id
            )}
          >
            More Info
          </Link>
        ),
      },
      {
        title: "",
        dataIndex: "id",
        render: (id, record) => (
          <RemovePopconfirm
            title="Remove Installed Device"
            description="Are you sure you want to remove this installed device?"
            handleDeleteClick={() => {
              updateInstalledDevices({
                variables: {
                  id: id,
                  set: {
                    deleted_at: new Date(),
                    deleted_by: loggedUserValue.id,
                  },
                },
              });
            }}
          />
        ),
      },
    ],
    [showPropertyColumn]
  );

  const distinctAttachmentTypes = useMemo(() => {
    const types = (product?.product_attachments ?? [])
      .map((a: any) => a.type)
      .filter(Boolean);
    return ([...new Set(types)] as string[]).sort((a, b) => a.localeCompare(b));
  }, [product?.product_attachments]);

  const [typeDropdownOptions, typeDropdownLabel = "All Types"] = useMemo(() => {
    const allSelected =
      selectedTypeFilter.length === 0 ||
      selectedTypeFilter.length === distinctAttachmentTypes.length;
    let selectedTypeName: string | undefined;

    if (distinctAttachmentTypes.length) {
      const items: MenuProps["items"] = distinctAttachmentTypes.reduce(
        (options, type, currentIndex) => {
          const optionId = `type-dropdown-option-${currentIndex + 1}-${type}`;
          const isSelected = selectedTypeFilter.includes(type);

          if (!selectedTypeName && isSelected) {
            selectedTypeName = AttachmentTypePluralizedDisplayNames[type as AttachmentType] ?? type;
          }

          options.push({
            key: type,
            icon: (
              <div>
                <input
                  className="custom-checkbox"
                  id={optionId}
                  type="checkbox"
                  onClick={(e) => e.stopPropagation()}
                  checked={isSelected}
                  onChange={voidFnc}
                />
                <label htmlFor={optionId} className="custom-checkbox-label">
                  {AttachmentTypePluralizedDisplayNames[type as AttachmentType] ?? type}
                </label>
              </div>
            ),
          });

          return options;
        },
        [
          {
            key: "type-dropdown-option-all",
            icon: (
              <div>
                <input
                  className="custom-checkbox"
                  id="type-dropdown-option-all"
                  type="checkbox"
                  onClick={(e) => e.stopPropagation()}
                  checked={allSelected}
                  onChange={voidFnc}
                />
                <label
                  htmlFor="type-dropdown-option-all"
                  className="custom-checkbox-label"
                >
                  All Types
                </label>
              </div>
            ),
          },
        ] as NonNullable<MenuProps["items"]>
      );

      return [
        items,
        createLabelForFilters("All Types", selectedTypeFilter, selectedTypeName),
      ];
    }

    return [[], "All Types"];
  }, [distinctAttachmentTypes, selectedTypeFilter]);

  const filteredAttachments = useMemo(() => {
    const source = product?.product_attachments ?? [];
    const attachments: any[] =
      selectedTypeFilter.length === 0 ||
        selectedTypeFilter.length === distinctAttachmentTypes.length
        ? [...source]
        : source.filter((a: any) => selectedTypeFilter.includes(a.type));

    return attachments.sort((a, b) => {
      const typeCompare = (a.type ?? "").localeCompare(b.type ?? "");
      if (typeCompare !== 0) return typeCompare;
      return (a.name ?? "").localeCompare(b.name ?? "");
    });
  }, [product?.product_attachments, selectedTypeFilter, distinctAttachmentTypes]);

  return (
    <div className="admin-products-details-screen-container">
      {product ? (
        <Breadcrumb
          className="products-breadcrumbs"
          items={[
            {
              title: "My Devices",
              onClick: () => {
                updateProductsFilters({
                  selectedCategoriesFilter: [],
                });
                navigate(routeNames.ADMIN_DEVICES);
              },
            },
            {
              title: product?.product_category.label,
              onClick: () => {
                updateProductsFilters({
                  selectedCategoriesFilter: [product?.product_category.name || ""],
                });
                navigate(routeNames.ADMIN_DEVICES);
              },
            },
            {
              title: product?.name,
            },
          ]}
        />
      ) : null}
      <div className="product-details-container">
        <div className="admin-product-details-row">
          <span className="product-d-name">{product?.name}</span>
          <div className="admin-product-details-icons">
            <img
              alt={`edit-attachment-icon`}
              src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
              className="selected-file-icon"
              onClick={handleOnEditProduct}
            />
            <RemovePopconfirm
              title="Remove Device"
              description="Are you sure you want to remove this device?"
              handleDeleteClick={handleRemove}
            />
          </div>
        </div>

        <div className="product-d-row">
          <div className="product-d-image-container">
            {product?.photo_link ? (
              <img src={product.photo_link} className="product-d-image" />
            ) : null}
          </div>
          <div className="admin-product-details-right-container">
            {product?.product_attachments?.length > 0 && (
              <div>
                <Dropdown
                  menu={{
                    items: typeDropdownOptions,
                    selectable: true,
                    multiple: true,
                    onSelect: ({ key }) => handleTypeSelection(key),
                    onDeselect: ({ key }) => handleTypeSelection(key),
                  }}
                  trigger={["click"]}
                  open={typeDropdownOpen}
                  onOpenChange={(
                    open: boolean,
                    info: { source: "trigger" | "menu" }
                  ) => {
                    if (info.source === "trigger" && open === false) {
                      setTypeDropdownOpen(false);
                    }
                  }}
                  placement="bottomLeft"
                  className="products-dropdown"
                  overlayClassName="products-dropdown-overlay"
                  arrow
                  disabled={distinctAttachmentTypes.length === 0}
                >
                  <Button
                    onClick={() => {
                      setTypeDropdownOpen((previousValue) => !previousValue);
                    }}
                  >
                    {typeDropdownLabel}
                    <img
                      src={
                        process.env.PUBLIC_URL +
                        "/assets/images/icons/black-chevron-down.svg"
                      }
                      title={typeDropdownLabel}
                      className="chevron-button"
                    />
                  </Button>
                </Dropdown>
              </div>
            )}
            <div className="product-d-attachments">
              {filteredAttachments.length ? filteredAttachments.map((attachment: any, index: number) => {
                let imgSrc = ``;
                let onClickAttachment;

                    switch (attachment.type) {
                      case AttachmentType.MANUAL:
                      case AttachmentType.GENERAL_DOCUMENT:
                      case AttachmentType.TECHNICAL_DOCUMENT:
                        imgSrc = `${process.env.PUBLIC_URL}/assets/images/icons/attachment-manual.svg`;
                        onClickAttachment = () => handleOnOpenPDF(attachment);
                        break;
                      case AttachmentType.PHOTO:
                      case AttachmentType.AUDIO:
                      case AttachmentType.VIDEO:
                        imgSrc = `${process.env.PUBLIC_URL}/assets/images/icons/attachment-video.svg`;
                        onClickAttachment = () => handleOnOpenVideo(attachment);
                        break;
                      case AttachmentType.DIAGRAM:
                        imgSrc = `${process.env.PUBLIC_URL}/assets/images/icons/attachment-doc.svg`;
                        onClickAttachment = () => handleOnOpenPDF(attachment);
                        break;
                      default:
                        return null;
                    }

                    return (
                      <div
                        className="admin-product-d-attachment"
                        onClick={onClickAttachment}
                        key={`product-d-attachment-${attachment.id}`}
                      >
                        <div className="icon-container">
                          <img
                            alt={attachment.type || `attachment-icon-${index}`}
                            src={imgSrc}
                          />
                        </div>
                        <span className="product-d-attachment-title">
                          {attachment.name || AttachmentTypeDisplayNames[attachment.type as AttachmentType] || attachment.type}
                        </span>
                      </div>
                    );
                  })
                : null}
            </div>
          </div>
        </div>
        <div className="product-d-separator"></div>

        {product?.make ? (
          <div className="product-d-data-container">
            <span className="product-d-data">{`Make: ${product?.make}`}</span>
          </div>
        ) : null}

        {product?.model ? (
          <div className="product-d-data-container">
            <span className="product-d-data">{`Model: ${product?.model}`}</span>
          </div>
        ) : null}

        {product?.sku ? (
          <div className="product-d-data-container">
            <span className="product-d-data">{`SKU: ${product?.sku}`}</span>
          </div>
        ) : null}

        {product?.retail_price ? (
          <div className="product-d-data-container">
            <span className="product-d-data">{`Retail Price: ${product?.retail_price}`}</span>
          </div>
        ) : null}

        <p className="product-d-data description">{product?.description}</p>

        {product?.product_notes ? (
          <>
            <span className="product-d-name">Notes</span>
            <span className="product-d-data">{product.product_notes}</span>
          </>
        ) : null}

        <div className="product-d-separator"></div>

        <Collapse
          ref={collapseLocationRef}
          className="device-d-table-collapse"
          size="large"
          items={[
            {
              key: "1",
              label: (
                <div className="organization-d-row">
                  <span className="organization-d-name">Locations</span>
                  {[
                    UserRoleValue.admin_user,
                    UserRoleValue.vendor_manager,
                    UserRoleValue.vendor_employee,
                  ].includes((loggedUserValue.role || "") as UserRoleValue) && (
                    <Link
                      to={{
                        pathname: routeNames.ADMIN_INSTALLED_DEVICE_ADD,
                        search:
                          "?deviceId=" +
                          params.deviceId +
                          (selectedPropertyFilter.length === 1
                            ? "&propertyId=" + selectedPropertyFilter[0]
                            : ""),
                      }}
                      style={{ display: "flex" }}
                    >
                      <img
                        alt={`edit-attachment-icon`}
                        src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle.svg`}
                        className="organization-plus-icon"
                      />
                    </Link>
                  )}
                </div>
              ),
              children: (
                <div className="device-d-table-container">
                  <Dropdown
                    menu={{
                      items: propertiesOptions,
                      selectable: true,
                      multiple: true,
                      onSelect: ({ key }) => handlePropertySelection(key),
                      onDeselect: ({ key }) => handlePropertySelection(key),
                    }}
                    trigger={["click"]}
                    open={propertiesDropdownOpen}
                    onOpenChange={(
                      open: boolean,
                      info: { source: "trigger" | "menu" }
                    ) => {
                      if (info.source === "trigger" && open === false) {
                        setPropertiesDropdownOpen(false);
                      }
                    }}
                    placement="bottomLeft"
                    className="products-dropdown"
                    overlayClassName="products-dropdown-overlay"
                    disabled={propertiesOptions.length === 0}
                    arrow
                  >
                    <Button
                      onClick={() => {
                        setPropertiesDropdownOpen(
                          (previousValue) => !previousValue
                        );
                      }}
                    >
                      {propertyDropdownLabel}
                      <img
                        src={
                          process.env.PUBLIC_URL +
                          "/assets/images/icons/black-chevron-down.svg"
                        }
                        title={propertyDropdownLabel}
                        className="chevron-button"
                      />
                    </Button>
                  </Dropdown>
                  <Table
                    className="device-d-table"
                    columns={locationsTableColumns}
                    dataSource={filteredInstallDevicesData}
                    pagination={false}
                    loading={loadingProduct}
                    rowKey="id"
                  />
                </div>
              ),
            },
          ]}
          expandIcon={({ isActive }) => <DropdownArrow isActive={isActive} />}
          expandIconPosition="end"
        />
      </div>

      <MediaPlayer
        onCancel={() => setFileViewer({ type: null, src: "" })}
        src={fileViewer.src}
        open={!!fileViewer.src && fileViewer.type !== null}
        mediaType={fileViewer.type}
      />
    </div>
  );
};

export default AdminProductDetails;
